import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const domains = [
  { icon: 'ri-palette-line', label: 'Design graphique' },
  { icon: 'ri-brush-2-line', label: 'Identité visuelle' },
  { icon: 'ri-layout-grid-line', label: 'Création de supports' },
];

function AboutHero() {
  return (
    <section className="relative min-h-[60vh] md:min-h-[70vh] flex items-end bg-[#1A1A1A] overflow-hidden pb-16 md:pb-24 px-6 md:px-10 lg:px-16 pt-28">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/ae3a6c0932de6cb077f27e4d5ce6380b.jpg"
          alt="Studio WABICH à Lille"
          className="w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/60 to-transparent" />
      </div>

      <div className="relative z-10 max-w-3xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-px bg-gray-500" />
          <span
            className="text-xs tracking-widest uppercase text-gray-400"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            À propos
          </span>
        </div>
        <h1
          className="text-white mb-6"
          style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontWeight: 300,
            fontSize: 'clamp(40px, 6vw, 80px)',
            lineHeight: '1.05',
          }}
        >
          Un studio<br />
          indépendant basé à<br />
          <em style={{ fontStyle: 'italic' }}>Lille</em>
        </h1>
        <p
          className="text-gray-300 max-w-md text-base leading-relaxed"
          style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
        >
          WABICH est une activité indépendante de design créatif, spécialisée dans les solutions visuelles modernes et sur-mesure.
        </p>
      </div>
    </section>
  );
}

function AboutContent() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section className="bg-white py-24 md:py-32 px-6 md:px-10 lg:px-16">
      <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start max-w-7xl mx-auto">
        {/* Left */}
        <div
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-gray-300" />
            <span
              className="text-xs tracking-widest uppercase text-gray-400"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              La dirigeante
            </span>
          </div>

          <h2
            className="mb-6 text-gray-900"
            style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontWeight: 300,
              fontSize: 'clamp(28px, 3.5vw, 46px)',
              lineHeight: '1.15',
            }}
          >
            Joanna Maria <em style={{ fontStyle: 'italic' }}>Wabich</em>
          </h2>

          <p
            className="text-gray-500 text-sm leading-loose mb-6"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
          >
            Le studio est dirigé par Joanna Maria Wabich, designer spécialisée dans la création de solutions visuelles modernes. Passionnée par le design sous toutes ses formes, elle accompagne ses clients dans la construction d&apos;une image forte, cohérente et mémorable.
          </p>

          <p
            className="text-gray-500 text-sm leading-loose mb-10"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
          >
            L&apos;objectif est clair : proposer des solutions créatives, professionnelles et parfaitement adaptées à chaque client, quelle que soit la taille du projet ou du budget.
          </p>

          {/* Designer portrait */}
          <div className="relative aspect-square max-w-sm rounded-xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20female%20designer%20portrait%20modern%20minimalist%20studio%20clean%20white%20background%20elegant%20professional%20woman%20creative%20designer%20Lille%20France%20sophisticated%20refined%20monochromatic%20neutral%20tones%20natural%20light%20confident%20feminine%20professional%20photography%20studio&width=400&height=400&seq=wabich-joanna-001&orientation=squarish"
              alt="Joanna Maria Wabich – Designer à Lille"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        {/* Right */}
        <div
          className={`transition-all duration-700 delay-150 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-gray-300" />
            <span
              className="text-xs tracking-widest uppercase text-gray-400"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Domaines d&apos;intervention
            </span>
          </div>

          <h3
            className="mb-8 text-gray-900"
            style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontWeight: 300,
              fontSize: 'clamp(24px, 2.5vw, 36px)',
              lineHeight: '1.2',
            }}
          >
            Des services <em style={{ fontStyle: 'italic' }}>créatifs</em><br />
            complets &amp; sur-mesure
          </h3>

          <div className="space-y-4 mb-12">
            {domains.map((domain, i) => (
              <div
                key={domain.label}
                className="flex items-center gap-5 p-5 border border-gray-100 rounded-lg hover:border-gray-300 transition-colors duration-300"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F5F3]">
                  <i className={`${domain.icon} text-gray-600 text-lg`} />
                </div>
                <span
                  className="text-gray-800 text-sm"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400 }}
                >
                  {domain.label}
                </span>
              </div>
            ))}
          </div>

          {/* Company info block */}
          <div className="bg-[#F8F8F6] rounded-xl p-8">
            <p
              className="text-xs tracking-widest uppercase text-gray-400 mb-5"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Informations entreprise
            </p>
            <dl className="space-y-3">
              {[
                { label: 'Entreprise', value: 'WABICH' },
                { label: 'Statut', value: 'Entrepreneur individuel' },
                { label: 'SIRET', value: '102 006 269 00017' },
                { label: 'Activité', value: '74.10Z – Design' },
                { label: 'Ville', value: 'Lille (59800), France' },
              ].map((item) => (
                <div key={item.label} className="flex justify-between gap-4 py-2 border-b border-gray-200 last:border-0">
                  <dt
                    className="text-xs text-gray-400 whitespace-nowrap"
                    style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
                  >
                    {item.label}
                  </dt>
                  <dd
                    className="text-xs text-gray-700 text-right"
                    style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400 }}
                  >
                    {item.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutValues() {
  const { ref, isVisible } = useScrollReveal();

  const values = [
    {
      icon: 'ri-focus-3-line',
      title: 'Précision',
      description: 'Chaque élément visuel est travaillé avec soin pour un résultat propre et professionnel.',
    },
    {
      icon: 'ri-heart-line',
      title: 'Passion',
      description: 'Le design créatif est plus qu\'un métier — c\'est une démarche qui donne du sens à chaque projet.',
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Proximité',
      description: 'Une relation directe avec chaque client pour comprendre ses besoins et dépasser ses attentes.',
    },
  ];

  return (
    <section className="bg-[#F8F8F6] py-24 md:py-28 px-6 md:px-10 lg:px-16">
      <div ref={ref} className="max-w-7xl mx-auto">
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2
            className="text-gray-900"
            style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontWeight: 300,
              fontSize: 'clamp(28px, 3vw, 44px)',
              lineHeight: '1.2',
            }}
          >
            Nos <em style={{ fontStyle: 'italic' }}>valeurs</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
          {values.map((value, i) => (
            <div
              key={value.title}
              className={`text-center p-8 md:p-10 bg-white rounded-xl transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 100 + 200}ms` }}
            >
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5">
                <i className={`${value.icon} text-3xl text-gray-400`} />
              </div>
              <h3
                className="mb-3 text-gray-900"
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontWeight: 500,
                  fontSize: '22px',
                }}
              >
                {value.title}
              </h3>
              <p
                className="text-sm text-gray-500 leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
              >
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Navbar />
      <main>
        <AboutHero />
        <AboutContent />
        <AboutValues />
      </main>
      <Footer />
    </div>
  );
}
